// ─────────────────────────────────────────────────────────────────────────────
// CRI — câblage ERP (lecture seule sur l'existant).
//
// Ce module LIT les réceptions, OF, factures de frais généraux et pointages
// déjà saisis, et ÉCRIT uniquement dans ses 4 collections analytiques :
//   oliva.standards_industriels         — barèmes versionnés (TND/t)
//   oliva.charges_industrielles         — charges réelles incorporables
//   oliva.imputations_industrielles_of  — coût standard figé par OF
//   oliva.regularisations_industrielles — écarts mensuels et de clôture
//
// Une imputation est ANALYTIQUE : elle ne crée jamais une deuxième facture
// fournisseur, ni un paiement, ni une écriture de caisse. Le CMP Achat des
// cuves n'est jamais touché : le CRI est une information supplémentaire.
// ─────────────────────────────────────────────────────────────────────────────
import {
  fetchOfs,
  fetchReceptions,
  fetchDepenses,
  fetchPointages,
  fetchVentes,
  decompte,
  bornesCampagne,
  prixReference,
  familleLot,
  tracer,
} from './dataProvider';
import {
  CATEGORIES_INDUSTRIELLES,
  chargeImputable,
  chargeIncorporable,
  construireStandard,
  coutSalarial,
  ecartIndustriel,
  ficheOf,
  r3,
  repartirProrata,
  soldeRegularisation,
  sousActivite,
} from './criCalculs';

export { CATEGORIES_INDUSTRIELLES };

const CLE_STANDARDS = 'oliva.standards_industriels';
const CLE_CHARGES = 'oliva.charges_industrielles';
const CLE_IMPUTATIONS = 'oliva.imputations_industrielles_of';
const CLE_REGULS = 'oliva.regularisations_industrielles';

const lire = (cle) => {
  try {
    return JSON.parse(localStorage.getItem(cle) ?? '[]');
  } catch {
    return [];
  }
};
const ecrire = (cle, v) => localStorage.setItem(cle, JSON.stringify(v));

export const LIGNES_PRODUCTION = ['Ligne principale', 'Repassage grignons'];
// CNSS employeur, régime général tunisien — surchargeable à chaque import de paie.
export const TAUX_CHARGES_PATRONALES_DEFAUT = 16.57;

export const campagneCourante = () => bornesCampagne().libelle;
export const bornesDeCampagne = (libelle) => {
  const an = Number(String(libelle || '').split('/')[0]);
  if (!Number.isFinite(an)) return { debut: '', fin: '' };
  return { debut: `${an}-09-01`, fin: `${an + 1}-08-31` };
};

// ───────── Standards industriels versionnés ─────────

export async function fetchStandards() {
  return lire(CLE_STANDARDS).sort((a, b) =>
    a.applicableDu === b.applicableDu ? b.version - a.version : a.applicableDu < b.applicableDu ? 1 : -1
  );
}

// Une nouvelle version s'applique aux PRODUCTIONS FUTURES : les OF déjà figés
// conservent leur version d'origine.
export async function creerVersionStandard({ campagne, ligne, applicableDu, categories, capaciteNormaleT, notes, utilisateur }) {
  if (!campagne) return { error: 'Précisez la campagne.' };
  if (!LIGNES_PRODUCTION.includes(ligne)) return { error: 'Choisissez la ligne de production.' };
  if (!applicableDu) return { error: 'Saisissez la date de début d’application.' };
  const calc = construireStandard({ categories, capaciteNormaleT });
  if (!(calc.tauxTndParTonne > 0))
    return { error: 'Saisissez au moins un coût variable par tonne ou une charge fixe.' };
  if (calc.fixesTnd > 0 && !(calc.capaciteNormaleT > 0))
    return { error: 'Une capacité normale (tonnes) est obligatoire pour répartir les charges fixes.' };
  const rows = lire(CLE_STANDARDS);
  const version =
    rows.filter((s) => s.campagne === campagne && s.ligne === ligne).reduce((m, s) => Math.max(m, s.version), 0) + 1;
  const row = {
    id: `STD-${Date.now()}`,
    campagne,
    ligne,
    version,
    applicableDu,
    ...calc,
    notes: String(notes || '').trim(),
    creeLe: new Date().toISOString(),
    creePar: utilisateur ?? '—',
  };
  ecrire(CLE_STANDARDS, [row, ...rows]);
  tracer({
    action: 'Création',
    entite: 'Standard industriel',
    refId: `${ligne} v${version}`,
    resume: `${calc.tauxTndParTonne} TND/t applicable au ${applicableDu} (campagne ${campagne})`,
    utilisateur,
  });
  return { error: null, standard: row };
}

export async function supprimerStandard(id, utilisateur) {
  const s = lire(CLE_STANDARDS).find((x) => x.id === id);
  if (!s) return { error: 'Version introuvable.' };
  if (lire(CLE_IMPUTATIONS).some((i) => i.standardId === id))
    return { error: 'Cette version est déjà figée sur des OF : elle ne peut plus être supprimée.' };
  ecrire(CLE_STANDARDS, lire(CLE_STANDARDS).filter((x) => x.id !== id));
  tracer({ action: 'Suppression', entite: 'Standard industriel', refId: `${s.ligne} v${s.version}`, resume: 'Version supprimée (jamais imputée)', utilisateur });
  return { error: null };
}

export const standardApplicable = (standards, { campagne, ligne, date }) =>
  standards
    .filter((s) => s.campagne === campagne && s.ligne === ligne && String(s.applicableDu) <= String(date || '9999-12-31'))
    .sort((a, b) => (a.applicableDu === b.applicableDu ? b.version - a.version : a.applicableDu < b.applicableDu ? 1 : -1))[0] ?? null;

// ───────── Charges industrielles réelles ─────────

export async function fetchChargesIndustrielles({ campagne, ligne } = {}) {
  return lire(CLE_CHARGES)
    .filter((c) => (!campagne || c.campagne === campagne) && (!ligne || c.ligne === ligne))
    .sort((a, b) => (a.periodeDebut < b.periodeDebut ? 1 : -1));
}

export async function enregistrerCharge({
  campagne,
  ligne,
  categorie,
  libelle,
  ht = 0,
  montantTVA = 0,
  autresTaxes = 0,
  timbreFiscal = 0,
  periodeDebut,
  periodeFin,
  datePaiement = '',
  exceptionnel = false,
  utilisateur,
}) {
  if (!campagne) return { error: 'Précisez la campagne.' };
  if (!LIGNES_PRODUCTION.includes(ligne)) return { error: 'Choisissez la ligne de production.' };
  if (!CATEGORIES_INDUSTRIELLES.includes(categorie)) return { error: 'Choisissez la catégorie industrielle.' };
  if (!periodeDebut || !periodeFin) return { error: 'Saisissez la période de consommation (début et fin).' };
  if (periodeFin < periodeDebut) return { error: 'La fin de période précède son début.' };
  const inc = chargeIncorporable({ ht, montantTVA, autresTaxes, timbreFiscal, partTvaNonRecuperable: 1 });
  if (!(inc.montantIncorporable > 0)) return { error: 'Le montant incorporable doit être supérieur à zéro.' };
  const row = {
    id: `CHI-${Date.now()}`,
    campagne,
    ligne,
    categorie,
    libelle: String(libelle || '').trim() || categorie,
    pieceRef: `MAN-${Date.now()}`,
    pieceNumero: '—',
    source: 'saisie',
    ...inc,
    periodeDebut,
    periodeFin,
    datePaiement,
    exceptionnel: exceptionnel === true,
    creeLe: new Date().toISOString(),
    creePar: utilisateur ?? '—',
  };
  ecrire(CLE_CHARGES, [row, ...lire(CLE_CHARGES)]);
  return { error: null, charge: row };
}

// Factures de frais généraux encore non incorporées (anti-doublon par pièce).
export async function fetchDepensesIndustrialisables() {
  const refs = new Set(lire(CLE_CHARGES).map((c) => c.pieceRef));
  return (await fetchDepenses()).filter((d) => !refs.has(`DEP:${d.id}`));
}

// Import analytique : le TTC de la facture est incorporé (TVA non récupérable
// à 100 %), le détail HT / TVA / autres taxes reste conservé pour l'expliquer.
// Aucune écriture fournisseur ni de paiement n'est créée ici.
export async function importerDepensesIndustrielles({ ids, campagne, ligne, categorie, periodeDebut, periodeFin, exceptionnel = false, utilisateur }) {
  if (!campagne) return { error: 'Précisez la campagne.' };
  if (!LIGNES_PRODUCTION.includes(ligne)) return { error: 'Choisissez la ligne de production.' };
  if (!CATEGORIES_INDUSTRIELLES.includes(categorie)) return { error: 'Choisissez la catégorie industrielle.' };
  if (!ids?.length) return { error: 'Sélectionnez au moins une facture.' };
  if (!periodeDebut || !periodeFin) return { error: 'Saisissez la période de consommation ou de service.' };
  const deps = await fetchDepenses();
  const rows = lire(CLE_CHARGES);
  const ajouts = [];
  const ignores = [];
  ids.forEach((id, i) => {
    const d = deps.find((x) => x.id === id);
    if (!d) return;
    const pieceRef = `DEP:${d.id}`;
    if (rows.some((r) => r.pieceRef === pieceRef)) {
      ignores.push(d.numeroFacture);
      return;
    }
    const inc = chargeIncorporable({
      ht: d.montantHT,
      montantTVA: d.montantTVA,
      autresTaxes: d.autresTaxes,
      timbreFiscal: d.timbreFiscal,
      partTvaNonRecuperable: 1,
    });
    ajouts.push({
      id: `CHI-${Date.now()}-${i}`,
      campagne,
      ligne,
      categorie,
      libelle: d.libelle || d.categorie,
      pieceRef,
      pieceNumero: d.numeroFacture,
      fournisseur: d.fournisseur,
      source: 'facture',
      ...inc,
      // Rattachement à la période de consommation, jamais à la date de paiement.
      periodeDebut,
      periodeFin,
      datePaiement: (d.reglements ?? [])[0]?.date ?? '',
      exceptionnel: exceptionnel === true,
      creeLe: new Date().toISOString(),
      creePar: utilisateur ?? '—',
    });
  });
  if (!ajouts.length) return { error: `Facture(s) déjà incorporée(s) : ${ignores.join(', ') || '—'}.` };
  ecrire(CLE_CHARGES, [...ajouts, ...rows]);
  return {
    error: null,
    ajoutes: ajouts.length,
    ignores,
    total: r3(ajouts.reduce((s, a) => s + a.montantIncorporable, 0)),
  };
}

// Masse salariale : coût de production (brut pointé + charges patronales),
// aucune TVA. Un pointage déjà importé n'est jamais recompté (pièce = période).
export async function importerMasseSalariale({ campagne, ligne, periodeDebut, periodeFin, tauxChargesPatronales = TAUX_CHARGES_PATRONALES_DEFAUT, exceptionnel = false, utilisateur }) {
  if (!campagne) return { error: 'Précisez la campagne.' };
  if (!LIGNES_PRODUCTION.includes(ligne)) return { error: 'Choisissez la ligne de production.' };
  if (!periodeDebut || !periodeFin) return { error: 'Saisissez la période de paie.' };
  const pieceRef = `PAIE:${ligne}:${periodeDebut}:${periodeFin}`;
  const rows = lire(CLE_CHARGES);
  if (rows.some((r) => r.pieceRef === pieceRef))
    return { error: 'Cette période de paie est déjà incorporée pour cette ligne (aucun double comptage).' };
  const pointages = (await fetchPointages()).filter((p) => p.date >= periodeDebut && p.date <= periodeFin);
  const brut = r3(pointages.reduce((s, p) => s + (Number(p.montant) || 0), 0));
  if (!(brut > 0)) return { error: 'Aucun pointage sur cette période.' };
  const cout = coutSalarial({ brut, tauxChargesPatronales });
  const row = {
    id: `CHI-${Date.now()}`,
    campagne,
    ligne,
    categorie: 'Main-d’œuvre',
    libelle: `Masse salariale pointée du ${periodeDebut} au ${periodeFin} (${pointages.length} pointage(s))`,
    pieceRef,
    pieceNumero: `PAIE-${periodeDebut}`,
    source: 'paie',
    ht: cout.brut,
    montantTVA: 0,
    autresTaxes: cout.chargesPatronales,
    timbreFiscal: 0,
    partTvaNonRecuperable: 1,
    tvaNonRecuperable: 0,
    montantIncorporable: cout.montantIncorporable,
    tauxChargesPatronales: cout.tauxChargesPatronales,
    pointages: pointages.length,
    periodeDebut,
    periodeFin,
    datePaiement: '',
    exceptionnel: exceptionnel === true,
    creeLe: new Date().toISOString(),
    creePar: utilisateur ?? '—',
  };
  ecrire(CLE_CHARGES, [row, ...rows]);
  return { error: null, charge: row, brut, total: cout.montantIncorporable };
}

export async function supprimerCharge(id) {
  const c = lire(CLE_CHARGES).find((x) => x.id === id);
  if (!c) return { error: 'Charge introuvable.' };
  ecrire(CLE_CHARGES, lire(CLE_CHARGES).filter((x) => x.id !== id));
  return { error: null };
}

// ───────── Fiches OF : matière (ERP) + transformation (standard) ─────────

export async function fetchFichesOf({ campagne } = {}) {
  const camp = campagne || campagneCourante();
  const { debut, fin } = bornesDeCampagne(camp);
  const [ofs, recs, standards] = await Promise.all([fetchOfs(), fetchReceptions(), fetchStandards()]);
  const imputations = lire(CLE_IMPUTATIONS);
  const regularisations = lire(CLE_REGULS);
  const aujourdhui = new Date().toISOString().slice(0, 10);

  return ofs
    .filter((o) => !debut || (String(o.date) >= debut && String(o.date) <= fin))
    .map((o) => {
      const lots = (o.lots ?? []).map((id) => recs.find((r) => r.id === id)).filter(Boolean);
      const prestation = lots.some((r) => r.parcours === 'client');
      const type = prestation ? 'prestation' : 'achat';
      // Coût matière : montant d'achat CONVENU, avant retenue Fonds des Calamités
      // et avant imputation d'avances (montantBrut, jamais montant net à payer).
      const M = prestation ? 0 : r3(lots.reduce((s, r) => s + decompte(r).montantBrut, 0));
      const H = r3(lots.reduce((s, r) => s + (Number(r.huileExtraite) || 0), 0));
      const Q = r3(lots.reduce((s, r) => s + decompte(r).poidsNet, 0) / 1000);
      const produitPrestation = prestation ? r3(lots.reduce((s, r) => s + decompte(r).trituration, 0)) : 0;
      const ligne = o.ligne || LIGNES_PRODUCTION[0];
      const imp = imputations.find((i) => i.ofId === o.id);
      const std = imp
        ? standards.find((s) => s.id === imp.standardId) ?? null
        : standardApplicable(standards, { campagne: camp, ligne, date: o.termineLe || o.date });
      const taux = imp ? imp.tauxTndParTonne : std?.tauxTndParTonne ?? 0;
      const deltaF = r3(
        regularisations
          .flatMap((r) => r.ajustements ?? [])
          .filter((a) => a.ofId === o.id)
          .reduce((s, a) => s + (Number(a.montant) || 0), 0)
      );
      const famille = o.famille || (lots.length ? familleLot(lots[0]) : 'noble');
      const prixRef = prixReference(famille);
      const V = prestation ? produitPrestation : r3(H * prixRef);
      const fiche = ficheOf({
        type,
        coutOlives: M,
        huileKg: H,
        tonnes: Q,
        tauxStandard: taux,
        deltaF,
        valeurCommerciale: V,
        produitPrestation,
      });
      const provisoire = lots.some((r) => decompte(r).provisoire);
      return {
        ...fiche,
        ofId: o.id,
        numero: o.numero,
        date: o.date,
        termineLe: o.termineLe ?? null,
        statutOf: o.statut,
        famille,
        ligne,
        campagne: camp,
        tiersNoms: o.tiersNoms,
        rendement: o.rendement,
        bonusKg: o.fosse?.kg ?? 0,
        prixReference: prixRef,
        dateReference: aujourdhui,
        prixMatiereProvisoire: provisoire,
        standardId: std?.id ?? imp?.standardId ?? null,
        standardVersion: imp?.version ?? std?.version ?? null,
        tauxStandard: taux,
        detailTaux: imp?.detail ?? std?.categories ?? [],
        fige: Boolean(imp),
        figeLe: imp?.figeLe ?? null,
        statutCout: !taux
          ? 'Aucun standard'
          : !imp
            ? 'Provisoire'
            : deltaF !== 0
              ? 'Régularisé'
              : 'Standard figé',
        // CMP Achat de l'OF : valeur MATIÈRE seule, jamais le CRI.
        cmpMatiereKg: prestation ? null : fiche.coutMatiereUnitaire,
      };
    })
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

export async function fetchImputations() {
  return lire(CLE_IMPUTATIONS);
}

// Fige la version du standard sur l'OF : les versions ultérieures ne s'appliquent
// qu'aux productions futures.
export async function figerImputation(ofId, utilisateur) {
  const fiches = await fetchFichesOf();
  const f = fiches.find((x) => x.ofId === ofId);
  if (!f) return { error: 'OF introuvable dans la campagne en cours.' };
  if (f.fige) return { error: 'Le coût standard de cet OF est déjà figé.' };
  if (!f.standardId) return { error: 'Aucun standard industriel applicable à la date de cet OF.' };
  if (!(f.Q > 0)) return { error: 'Aucune tonne traitée : rien à imputer.' };
  const standards = await fetchStandards();
  const std = standards.find((s) => s.id === f.standardId);
  const detail = (std?.categories ?? []).map((c) => ({
    categorie: c.categorie,
    tauxTndParTonne: c.tauxTndParTonne,
    montant: r3(f.Q * c.tauxTndParTonne),
  }));
  const row = {
    id: `IMP-${Date.now()}`,
    ofId,
    numero: f.numero,
    campagne: f.campagne,
    ligne: f.ligne,
    tonnes: f.Q,
    standardId: std.id,
    version: std.version,
    tauxTndParTonne: std.tauxTndParTonne,
    detail,
    montant: r3(detail.reduce((s, d) => s + d.montant, 0)),
    figeLe: new Date().toISOString(),
    figePar: utilisateur ?? '—',
  };
  ecrire(CLE_IMPUTATIONS, [row, ...lire(CLE_IMPUTATIONS)]);
  tracer({
    action: 'Imputation analytique',
    entite: 'CRI',
    refId: f.numero,
    resume: `${row.montant} TND de frais industriels standards (v${std.version}, ${f.Q} t)`,
    utilisateur,
  });
  return { error: null, imputation: row };
}

export async function annulerImputation(ofId, utilisateur) {
  const rows = lire(CLE_IMPUTATIONS);
  const imp = rows.find((i) => i.ofId === ofId);
  if (!imp) return { error: 'Aucune imputation figée sur cet OF.' };
  if (lire(CLE_REGULS).some((r) => (r.ajustements ?? []).some((a) => a.ofId === ofId)))
    return { error: 'OF déjà régularisé : annulez d’abord la régularisation concernée.' };
  ecrire(CLE_IMPUTATIONS, rows.filter((i) => i.ofId !== ofId));
  tracer({ action: 'Annulation', entite: 'CRI', refId: imp.numero, resume: 'Imputation standard annulée', utilisateur });
  return { error: null };
}

// ───────── Rapprochement charges réelles / standards ─────────

export async function fetchRegularisations({ campagne } = {}) {
  return lire(CLE_REGULS)
    .filter((r) => !campagne || r.campagne === campagne)
    .sort((a, b) => (a.creeLe < b.creeLe ? 1 : -1));
}

// Périmètre homogène : même campagne, même ligne — OF d'achat ET de prestation.
export async function rapprochement({ campagne, ligne, du, au }) {
  const camp = campagne || campagneCourante();
  const bornes = bornesDeCampagne(camp);
  const debut = du || bornes.debut;
  const fin = au || bornes.fin;
  const charges = (await fetchChargesIndustrielles({ campagne: camp, ligne })).filter((c) =>
    chargeImputable(c, { du: debut, au: fin })
  );
  const reelles = r3(charges.filter((c) => !c.exceptionnel).reduce((s, c) => s + c.montantIncorporable, 0));
  const exceptionnelles = r3(charges.filter((c) => c.exceptionnel).reduce((s, c) => s + c.montantIncorporable, 0));
  const fiches = (await fetchFichesOf({ campagne: camp })).filter(
    (f) => f.ligne === ligne && String(f.termineLe || f.date) >= debut && String(f.termineLe || f.date) <= fin
  );
  // Seuls les OF dont le standard est figé portent des frais standards imputés.
  const imputes = fiches.filter((f) => f.fige);
  const standardsImputes = r3(imputes.reduce((s, f) => s + f.F, 0));
  const tonnes = r3(imputes.reduce((s, f) => s + f.Q, 0));
  const ecart = ecartIndustriel(reelles, standardsImputes);

  // Idempotence : on ne réaffecte que le solde non encore comptabilisé sur le périmètre.
  const anterieures = (await fetchRegularisations({ campagne: camp })).filter(
    (r) => r.ligne === ligne && chargeImputable({ periodeDebut: r.du, periodeFin: r.au }, { du: debut, au: fin })
  );
  const dejaComptabilise = r3(anterieures.reduce((s, r) => s + (Number(r.ecartComptabilise) || 0), 0));
  const solde = soldeRegularisation(ecart, dejaComptabilise);
  const repartition = repartirProrata(solde, imputes.map((f) => f.Q));
  const ajustements = imputes.map((f, i) => ({
    ofId: f.ofId,
    numero: f.numero,
    type: f.type,
    tonnes: f.Q,
    montant: repartition.parts[i],
  }));

  const standards = await fetchStandards();
  const std = standardApplicable(standards, { campagne: camp, ligne, date: fin });
  const sousAct = sousActivite({
    capaciteNormaleT: std?.capaciteNormaleT ?? 0,
    tonnesReelles: tonnes,
    fixesTnd: std?.fixesTnd ?? 0,
  });

  return {
    campagne: camp,
    ligne,
    du: debut,
    au: fin,
    charges,
    reelles,
    exceptionnelles,
    fiches,
    imputes,
    ofsNonFiges: fiches.filter((f) => !f.fige),
    standardsImputes,
    tonnes,
    ecart,
    dejaComptabilise,
    solde,
    resteNonReparti: repartition.reste,
    ajustements,
    anterieures,
    sousActivite: sousAct,
    capaciteNormaleT: std?.capaciteNormaleT ?? null,
  };
}

// Régularisation mensuelle (provisoire) ou clôture de campagne (définitive).
// Relancer une clôture ne cumule JAMAIS une seconde fois le même écart.
export async function enregistrerRegularisation({ campagne, ligne, du, au, type = 'mensuelle', utilisateur }) {
  const rap = await rapprochement({ campagne, ligne, du, au });
  if (!rap.imputes.length)
    return { error: 'Aucun OF au coût standard figé sur ce périmètre : figez les imputations avant de régulariser.' };
  if (rap.solde === 0)
    return { error: 'Aucun solde restant à comptabiliser : les écarts de ce périmètre sont déjà régularisés.' };
  const row = {
    id: `REG-${Date.now()}`,
    campagne: rap.campagne,
    ligne,
    type,
    du: rap.du,
    au: rap.au,
    chargesReelles: rap.reelles,
    chargesExceptionnelles: rap.exceptionnelles,
    standardsImputes: rap.standardsImputes,
    ecart: rap.ecart,
    dejaComptabiliseAvant: rap.dejaComptabilise,
    ecartComptabilise: rap.solde,
    cleRepartition: 'Tonnes d’olives réellement traitées par OF',
    tonnes: rap.tonnes,
    ajustements: rap.ajustements,
    fixesNonAbsorbes: rap.sousActivite.fixesNonAbsorbes,
    tauxActivite: rap.sousActivite.tauxActivite,
    creeLe: new Date().toISOString(),
    creePar: utilisateur ?? '—',
  };
  ecrire(CLE_REGULS, [row, ...lire(CLE_REGULS)]);
  tracer({
    action: type === 'cloture' ? 'Clôture analytique' : 'Régularisation mensuelle',
    entite: 'CRI',
    refId: `${ligne} ${rap.du}→${rap.au}`,
    resume: `Écart ${rap.ecart} TND, solde comptabilisé ${rap.solde} TND sur ${rap.imputes.length} OF`,
    utilisateur,
  });
  return { error: null, regularisation: row };
}

export async function annulerRegularisation(id, utilisateur) {
  const rows = lire(CLE_REGULS);
  const r = rows.find((x) => x.id === id);
  if (!r) return { error: 'Régularisation introuvable.' };
  ecrire(CLE_REGULS, rows.filter((x) => x.id !== id));
  tracer({ action: 'Annulation', entite: 'CRI', refId: `${r.ligne} ${r.du}→${r.au}`, resume: `Régularisation de ${r.ecartComptabilise} TND annulée`, utilisateur });
  return { error: null };
}

// ───────── Résultat industriel de campagne ─────────

export async function resultatCampagne({ campagne } = {}) {
  const camp = campagne || campagneCourante();
  const { debut, fin } = bornesDeCampagne(camp);
  const fiches = await fetchFichesOf({ campagne: camp });
  const charges = await fetchChargesIndustrielles({ campagne: camp });
  const achats = fiches.filter((f) => f.type === 'achat');
  const prestations = fiches.filter((f) => f.type === 'prestation');

  const matiere = r3(achats.reduce((s, f) => s + f.M, 0));
  const huile = r3(achats.reduce((s, f) => s + f.H, 0));
  const tonnes = r3(fiches.reduce((s, f) => s + f.Q, 0));
  const fraisStandard = r3(fiches.reduce((s, f) => s + f.F, 0));
  const regularisation = r3(fiches.reduce((s, f) => s + f.deltaF, 0));
  const valeurEstimee = r3(achats.reduce((s, f) => s + f.V, 0));
  const produitPrestations = r3(prestations.reduce((s, f) => s + f.V, 0));
  const reelles = r3(charges.filter((c) => !c.exceptionnel).reduce((s, c) => s + c.montantIncorporable, 0));
  const exceptionnelles = r3(charges.filter((c) => c.exceptionnel).reduce((s, c) => s + c.montantIncorporable, 0));

  const standards = await fetchStandards();
  const std = standardApplicable(standards, { campagne: camp, ligne: LIGNES_PRODUCTION[0], date: fin });
  const sousAct = sousActivite({
    capaciteNormaleT: std?.capaciteNormaleT ?? 0,
    tonnesReelles: tonnes,
    fixesTnd: std?.fixesTnd ?? 0,
  });

  // Marge RÉALISÉE : uniquement les quantités vendues, au CRI régularisé moyen.
  // Le coût de l'huile encore en stock reste attaché à ce stock.
  const ventes = (await fetchVentes()).filter((v) => String(v.date) >= debut && String(v.date) <= fin);
  const kgVendus = r3(ventes.reduce((s, v) => s + (Number(v.kg) || 0), 0));
  const caVentes = r3(ventes.reduce((s, v) => s + (Number(v.total) || 0), 0));
  const criMoyen = huile > 0 ? r3((matiere + r3(achats.reduce((s, f) => s + f.F + f.deltaF, 0))) / huile) : null;
  const kgFactures = Math.min(kgVendus, huile);
  const coutVendu = criMoyen != null ? r3(kgFactures * criMoyen) : null;
  const stockKg = r3(Math.max(0, huile - kgVendus));

  return {
    campagne: camp,
    debut,
    fin,
    tonnes,
    huile,
    matiere,
    fraisStandard,
    regularisation,
    fraisDefinitifs: r3(fraisStandard + regularisation),
    valeurEstimee,
    produitPrestations,
    margeBruteMatiere: r3(valeurEstimee - matiere),
    margeIndustrielleStandard: r3(valeurEstimee + produitPrestations - matiere - fraisStandard),
    margeIndustrielleRegularisee: r3(valeurEstimee + produitPrestations - matiere - fraisStandard - regularisation),
    reelles,
    exceptionnelles,
    ecartNonRegularise: r3(reelles - fraisStandard - regularisation),
    sousActivite: sousAct,
    criMoyen,
    kgVendus,
    caVentes,
    coutVendu,
    margeRealisee: coutVendu != null ? r3(caVentes - coutVendu) : null,
    stockKg,
    valeurStockCri: criMoyen != null ? r3(stockKg * criMoyen) : null,
    nbOfs: fiches.length,
    nbFiges: fiches.filter((f) => f.fige).length,
  };
}
